import React, { useContext } from "react";
import {
    Card,
    CardHeader,
    CardContent,
    CardFooter,
} from "../../components/ui/Card";
import { Button } from "../../components/ui/Button";
import { useAuth } from "src/contexts/AuthContext";
import { useLoading } from "src/contexts/LoadingContext";
import { InterviewContext } from "src/contexts/InterviewContext";
import usePartnerAwareNavigate from "src/hooks/usePartnerAwareNavigate";
import {
    BackendCase,
    transformCaseToInterviewCase,
} from "src/hooks/useFetchCases";
import {
    Clock,
    Building,
    Briefcase,
    Calendar,
    Award,
    Users,
} from "lucide-react";
import { Timestamp } from "firebase/firestore";

const StartContestCard: React.FC<{
    contestId: string;
    interviewCase: BackendCase;
    onGoing: boolean;
    hasDone: boolean;
    participantNumber: number;
    startTime?: { _seconds: number; _nanoseconds: number } | Timestamp;
    endTime?: { _seconds: number; _nanoseconds: number } | Timestamp;
}> = ({
    contestId,
    interviewCase,
    onGoing,
    hasDone,
    participantNumber,
    startTime,
    endTime,
}) => {
    const navigate = usePartnerAwareNavigate();
    const { firebaseUser } = useAuth();
    const { handleTimeBasedLoading } = useLoading();
    const { startInterviewHandler, settIsEndHandler } =
        useContext(InterviewContext)!;
    const frontendInterviewCase = transformCaseToInterviewCase(interviewCase);

    const handleStartMockInterview = async (caseId: number) => {
        try {
            const didStart = await handleTimeBasedLoading(
                startInterviewHandler(
                    firebaseUser?.uid ?? "",
                    caseId,
                    false,
                    true,
                    contestId
                ),
                {
                    totalDuration: 3500,
                    messages: [
                        "Initializing your mock interview...",
                        "Setting up your case materials...",
                        "Preparing interview environment...",
                        "Almost ready to begin...",
                    ],
                    onComplete: () => {
                        console.log("Loading complete");
                    },
                }
            );

            if (didStart && typeof didStart === "string") {
                settIsEndHandler(false);
                navigate(`/mock-case?interviewSessionId=${didStart}`, {
                    state: { interviewCase: frontendInterviewCase },
                });
            } else {
                console.error(
                    "Interview could not be started. Please try again."
                );
            }
        } catch (err) {
            console.error("Error starting interview:", err);
        }
    };

    const handleActionClick = async (id: number, variant: string) => {
        if (!firebaseUser) {
            navigate("/login");
            return;
        }

        if (variant === "Start") {
            await handleStartMockInterview(id);
        }
    };

    // Function to convert timestamp to Date
    const toDate = (timestamp: any): Date | null => {
        if (!timestamp) return null;

        // If it's already a Timestamp instance
        if (timestamp instanceof Date) return timestamp;

        // If it's a serialized Timestamp with _seconds and _nanoseconds
        if (
            timestamp._seconds !== undefined &&
            timestamp._nanoseconds !== undefined
        ) {
            return new Date(timestamp._seconds * 1000);
        }

        return null;
    };

    // Check if contest is upcoming (current time < start time)
    const isUpcoming = startTime ? new Date() < toDate(startTime)! : false;

    return (
        <div className="container mx-auto px-4 py-6 max-w-6xl">
            <Card className="shadow-md hover:shadow-xl transition-all duration-300">
                <CardHeader className="bg-gradient-to-r from-blue-50 to-indigo-50 border-b">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                        <div>
                            <h2 className="text-3xl font-bold text-gray-800 mb-2">
                                {frontendInterviewCase.title}
                            </h2>
                            <div className="flex items-center gap-2">
                                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                                    {frontendInterviewCase.isInterviewerLed
                                        ? "Interviewer-led"
                                        : "Interviewee-led"}
                                </span>
                                <span className="text-gray-500">•</span>
                                <span className="flex items-center text-gray-600 text-sm">
                                    <Award size={16} className="mr-1" />
                                    Case Competition
                                </span>
                            </div>
                        </div>
                        {/* <div className="mt-4 md:mt-0">
                            <span className="text-sm text-gray-500">
                                Contest ID: {contestId}
                            </span>
                        </div> */}
                    </div>
                </CardHeader>

                <CardContent className="p-0">
                    <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x">
                        {/* Left Column - Case Details */}
                        <div className="p-6 flex flex-col h-full">
                            <h3 className="text-lg font-semibold mb-4 text-gray-800 border-b pb-2">
                                Case Details
                            </h3>

                            <div className="space-y-4 flex-grow">
                                {/* Industry */}
                                <div className="flex items-start">
                                    <Building
                                        size={20}
                                        className="text-blue-600 mr-3 mt-1"
                                    />
                                    <div>
                                        <p className="text-sm font-medium text-gray-500">
                                            Industry
                                        </p>
                                        <p className="font-semibold text-gray-800">
                                            {frontendInterviewCase.industry}
                                        </p>
                                    </div>
                                </div>

                                {/* Company */}
                                <div className="flex items-start">
                                    <Briefcase
                                        size={20}
                                        className="text-blue-600 mr-3 mt-1"
                                    />
                                    <div>
                                        <p className="text-sm font-medium text-gray-500">
                                            Company
                                        </p>
                                        <p className="font-semibold text-gray-800">
                                            {frontendInterviewCase.company}
                                        </p>
                                    </div>
                                </div>

                                {/* Time */}
                                <div className="flex items-start">
                                    <Clock
                                        size={20}
                                        className="text-blue-600 mr-3 mt-1"
                                    />
                                    <div>
                                        <p className="text-sm font-medium text-gray-500">
                                            Estimated Time
                                        </p>
                                        <p className="font-semibold text-gray-800">
                                            {frontendInterviewCase.expectedTime}
                                        </p>
                                    </div>
                                </div>

                                {/* Participants */}
                                <div className="flex items-start">
                                    <Users
                                        size={20}
                                        className="text-blue-600 mr-3 mt-1"
                                    />
                                    <div>
                                        <p className="text-sm font-medium text-gray-500">
                                            Participants
                                        </p>
                                        <p className="font-semibold text-gray-800">
                                            {participantNumber} registered
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Middle Column - Topics & Skills */}
                        <div className="p-6 flex flex-col h-full">
                            <h3 className="text-lg font-semibold mb-4 text-gray-800 border-b pb-2">
                                Topics & Skills
                            </h3>

                            <div className="flex-grow">
                                <p className="text-sm text-gray-600 mb-3">
                                    This case will test your knowledge and
                                    skills in the following areas:
                                </p>

                                <div className="flex flex-wrap gap-2 mb-6">
                                    {frontendInterviewCase.topics.map(
                                        (topic, idx) => (
                                            <span
                                                key={idx}
                                                className={`
                                                px-3 py-1.5 text-sm font-medium 
                                                rounded-full 
                                                transition-all duration-200 
                                                hover:shadow-md
                                                ${topic.color}
                                            `}>
                                                {topic.name}
                                            </span>
                                        )
                                    )}
                                </div>

                                <div className="mt-4">
                                    <h4 className="font-medium text-gray-700 mb-2">
                                        Recommended Preparation
                                    </h4>
                                    <ul className="list-disc pl-5 space-y-1 text-sm text-gray-600">
                                        <li>Review market sizing techniques</li>
                                        <li>Practice structured frameworks</li>
                                        <li>Brush up on business metrics</li>
                                        <li>Study similar industry cases</li>
                                    </ul>
                                </div>
                            </div>
                        </div>

                        {/* Right Column - Timeline */}
                        <div className="p-6 flex flex-col h-full bg-gray-50">
                            <h3 className="text-lg font-semibold mb-4 text-gray-800 border-b pb-2">
                                Timeline & Submission
                            </h3>

                            <div className="space-y-4 flex-grow">
                                <div className="space-y-4">
                                    {/* Start Time */}
                                    <div className="flex items-start">
                                        <Calendar
                                            size={20}
                                            className="text-purple-600 mr-3 mt-1"
                                        />
                                        <div>
                                            <p className="text-sm font-medium text-gray-500">
                                                Contest Start
                                            </p>
                                            <p className="font-semibold text-gray-800">
                                                {startTime
                                                    ? toDate(
                                                          startTime
                                                      )?.toLocaleDateString(
                                                          "en-US",
                                                          {
                                                              weekday: "long",
                                                              year: "numeric",
                                                              month: "long",
                                                              day: "numeric",
                                                          }
                                                      )
                                                    : "TBD"}{" "}
                                                at 6:00 PM PT
                                            </p>
                                        </div>
                                    </div>

                                    {/* Submission */}
                                    <div className="flex items-start">
                                        <Calendar
                                            size={20}
                                            className="text-red-600 mr-3 mt-1"
                                        />
                                        <div>
                                            <p className="text-sm font-medium text-gray-500">
                                                Submission Deadline
                                            </p>
                                            <p className="font-semibold text-gray-800">
                                                {startTime
                                                    ? (() => {
                                                          const startDate =
                                                              toDate(startTime);
                                                          if (!startDate)
                                                              return "TBD";
                                                          const submissionDate =
                                                              new Date(
                                                                  startDate.getTime() +
                                                                      2 *
                                                                          86400000
                                                              );
                                                          return `${submissionDate.toLocaleDateString(
                                                              "en-US",
                                                              {
                                                                  weekday:
                                                                      "long",
                                                                  year: "numeric",
                                                                  month: "long",
                                                                  day: "numeric",
                                                              }
                                                          )} at 6:00 PM PT`;
                                                      })()
                                                    : "TBD"}
                                            </p>
                                        </div>
                                    </div>

                                    {/* Results */}
                                    <div className="flex items-start">
                                        <Calendar
                                            size={20}
                                            className="text-green-600 mr-3 mt-1"
                                        />
                                        <div>
                                            <p className="text-sm font-medium text-gray-500">
                                                Results Announced
                                            </p>
                                            <p className="font-semibold text-gray-800">
                                                {startTime
                                                    ? (() => {
                                                          const startDate =
                                                              toDate(startTime);
                                                          if (!startDate)
                                                              return "TBD";
                                                          const announcementDate =
                                                              new Date(
                                                                  startDate.getTime() +
                                                                      3 *
                                                                          86400000
                                                              );
                                                          return `${announcementDate.toLocaleDateString(
                                                              "en-US",
                                                              {
                                                                  weekday:
                                                                      "long",
                                                                  year: "numeric",
                                                                  month: "long",
                                                                  day: "numeric",
                                                              }
                                                          )} at 5:00 PM PT`;
                                                      })()
                                                    : "TBD"}
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                <div className="border-t pt-4 mt-4">
                                    <p className="text-sm text-gray-600 mb-2">
                                        <span className="font-medium">
                                            Note:
                                        </span>{" "}
                                        You will have one attempt to submit your
                                        solution. Make sure you're well prepared
                                        before starting.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </CardContent>

                <CardFooter className="border-t p-6">
                    <div className="flex flex-col sm:flex-row justify-between items-center w-full gap-4">
                        <div className="text-sm text-gray-600">
                            {hasDone
                                ? "You have already submitted your solution for this contest."
                                : onGoing
                                ? "Click the button to start your mock interview session. Remember, you only get one submission!"
                                : isUpcoming
                                ? "This contest will start soon. Please check back when the contest is active."
                                : "This contest has ended. No new submissions are accepted. Check back for the next contest!"}
                        </div>
                        <Button
                            className="
                                px-8 py-3 sm:w-auto w-full
                                bg-blue-600 hover:bg-blue-700
                                text-white font-medium text-base
                                rounded-lg
                                transition-all duration-200 
                                active:scale-95
                                shadow-md hover:shadow-lg
                            "
                            disabled={hasDone || !onGoing}
                            onClick={() =>
                                handleActionClick(
                                    frontendInterviewCase.id,
                                    "Start"
                                )
                            }>
                            {hasDone
                                ? "Already Submitted"
                                : onGoing
                                ? "Start Mock Interview"
                                : isUpcoming
                                ? "Coming Soon"
                                : "Contest Ended"}
                        </Button>
                    </div>
                </CardFooter>
            </Card>
        </div>
    );
};

export default StartContestCard;
